import React, { FC, memo, useCallback, useEffect, useState } from 'react'
import { Text, Link } from '@sitecore-jss/sitecore-jss-react'
import { PrimaryHeaderMenuLink } from '../../types/header.types'
import { useMemorySafeTimeout } from '../../../../hooks/useMemorySafeTimeout'
import { useCompareRoute } from '../../hooks/useCompareRoute'
import './HeaderLinks.scss'

import RoutableSitecoreLink from '../../../../react-only-components/RoutableSitecoreLink'

interface HeaderLinksProps {
  isHover: boolean
  primaryHeaderMenuLinks: PrimaryHeaderMenuLink[]
  onDrawerStateChange: (state: 'open' | 'closed') => void
}

const HeaderLinks: FC<HeaderLinksProps> = memo(
  ({ isHover, primaryHeaderMenuLinks, onDrawerStateChange }) => {
    const setSafeTimeout = useMemorySafeTimeout()
    const compare = useCompareRoute()

    const [activeLevel, setActiveLevel] = useState<string>('')
    const [prevLevel, setPrevLevel] = useState<string>('')

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [isTransitioning, setIsTransitioning] = useState<boolean>(false)

    let pathName = '';
    if (typeof window !== "undefined") {
      pathName = window?.location?.pathname;
    }

    const handleActiveLevelChange: (level: string) => void = useCallback(
      (level) => {
        if (level === activeLevel) {
          setIsOpen(false)
          setSafeTimeout(() => {
            setActiveLevel('')
            setPrevLevel('')
          }, 250)
          onDrawerStateChange('closed')
          setIsTransitioning(true)
        } else {
          setIsOpen(true)
          setPrevLevel(activeLevel)
          setActiveLevel(level)
          onDrawerStateChange('open')
          setIsTransitioning(true)
        }
      },
      [activeLevel]
    )

    // Keep drawer visible to end closing animation
    useEffect(() => {
      if (!isTransitioning) return
      setSafeTimeout(() => {
        setIsTransitioning(false)
      }, 250)
    }, [isTransitioning])

    // This sets the drawer visibility to hidden prematurely, should the mouse
    // leave the header (if the header is transparent) and make it transparent too.
    // -: add a "should-drawer-even-care-about-being-transparent" prop (e.g. page is not the root) - no longer needed
    useEffect(() => {
      if (!isHover && isTransitioning) setIsTransitioning(false)
    }, [isHover])

    return (
      <>
        <div className="Header__links_container">
          {primaryHeaderMenuLinks.map(
            ({ fields: { link, menuButton, section1 } }, idx) => (
              <div key={idx} className="Header__links_item">
                {(link.value.href?.length ?? 0) > 0 ? (
                  <a
                    href={link.value.href}
                    {...(link.value.linktype === 'internal'
                      ? {}
                      : { target: '_blank', rel: 'noopener noreferrer' })}
                  >
                    <Text field={menuButton} />
                    {compare(menuButton.value) && (
                      <div className="Header__active_bar" />
                    )}
                  </a>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        handleActiveLevelChange(menuButton.value)
                      }}
                    >
                      <Text field={menuButton} />
                    </button>
                    {(compare(menuButton.value) ||
                      section1
                        .map((s) => s.fields.link.value.href)
                        .includes(pathName)) && (
                      <div className="Header__active_bar" />
                    )}
                  </>
                )}
              </div>
            )
          )}
        </div>

        <div
          className={`Header__links_drawer ${
            isOpen ? 'Header__links_drawer--active' : ''
          } ${isTransitioning ? 'Header__links_drawer--animating' : ''}`}
        >
          <div>
            {primaryHeaderMenuLinks.map(
              (
                {
                  fields: {
                    section1: navLinks,
                    menuButton: { value: levelName },
                  },
                },
                idx
              ) => (
                <div
                  key={idx}
                  className={`Header__links_drawer_elements ${
                    prevLevel.length > 0
                      ? 'Header__links_drawer_elements--animate'
                      : ''
                  }`}
                  style={{
                    opacity: levelName === activeLevel ? 1 : 0,
                    zIndex: levelName === activeLevel ? 1 : 0,
                  }}
                >
                  {navLinks.map(({ fields: { link } }, i) => (
                    <Link
                      key={i}
                      className={
                        link.value.href === (pathName)
                          ? 'Header__links_drawer_main_link'
                          : ''
                      }
                      field={link}
                    />
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </>
    )
  }
)

export default HeaderLinks
